import React, { useCallback, useState, useEffect } from "react";
import ModernTemplateEditor from "../../../../altzero_plugins/features/template-editor/Editor/features/Editor/components/ModernEditor/ModernTemplateEditor";
import type {
  EditorSaveData,
  EditorSaveResult,
  EditorLoadResult,
  TemplateEditorConfig,
  TemplateEditorProps,
  EditorStorageConfig,
} from "../../../../altzero_plugins/features/template-editor/Editor/features/Editor/types/editor";
import {
  createCustomTemplate,
  updateCustomTemplate,
  loadTemplateContent,
} from "../../services/documentLibraryService";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";

interface JsonTemplateEditorProps {
  templateId?: string;
  templateName?: string;
  templateDescription?: string;
  initialJsonData?: any;
  onSave?: (data: EditorSaveData) => Promise<EditorSaveResult>;
  onChange?: (data: any) => void;
  readonly?: boolean;
  className?: string;
  isSaving?: boolean;
  storageConfig?: EditorStorageConfig;
}

/**
 * Wrapper component for ModernTemplateEditor that integrates with scopingAi template service
 * Handles conversion between EditorSaveData format and scopingAi database format
 */
export default function JsonTemplateEditor({
  templateId,
  templateName,
  templateDescription,
  initialJsonData,
  onSave,
  onChange,
  readonly = false,
  className = "",
  isSaving = false,
  storageConfig,
}: JsonTemplateEditorProps) {
  const { user } = useUser();
  const { toast } = useToast();
  const [initialData, setInitialData] = useState<any>(initialJsonData);
  const [loading, setLoading] = useState(false);

  // Load template data if templateId is provided
  useEffect(() => {
    const loadTemplate = async () => {
      if (templateId && !initialJsonData) {
        try {
          setLoading(true);
          const content = await loadTemplateContent(templateId);
          
          // Parse JSON content
          let parsedData;
          try {
            parsedData = typeof content === "string" ? JSON.parse(content) : content;
          } catch (e) {
            // If not valid JSON, create default structure
            parsedData = {
              blocks: [],
              canvasState: {},
              variables: [],
              pages: [],
            };
          }

          setInitialData({
            name: templateName || "",
            description: templateDescription || "",
            editorState: parsedData,
            metadata: {},
          });
        } catch (error) {
          console.error("Failed to load template:", error);
          toast({
            title: "Error",
            description: "Failed to load template content",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      } else if (initialJsonData) {
        setInitialData(initialJsonData);
      }
    };

    loadTemplate();
  }, [templateId, templateName, templateDescription, initialJsonData, toast]);

  // Extract sections from JSON blocks for database storage
  const extractSectionsFromBlocks = (blocks: any[]): string[] => {
    const sections: string[] = [];
    
    blocks.forEach((block) => {
      // Extract section titles from heading blocks
      if (
        block.type === "heading1" ||
        block.type === "heading2" ||
        block.type === "heading3"
      ) {
        const text = block.content?.text || block.content || "";
        if (text && text.trim()) {
          sections.push(text.trim());
        }
      }
      
      // Also check for text blocks that might be headings
      if (block.type === "text" && block.content) {
        const text = typeof block.content === "string" 
          ? block.content 
          : block.content.text || "";
        
        // Check if text looks like a heading (starts with # or is short and bold)
        if (text.trim().startsWith("#") || (text.length < 100 && block.style?.fontWeight === "bold")) {
          const cleanText = text.replace(/^#+\s*/, "").trim();
          if (cleanText) {
            sections.push(cleanText);
          }
        }
      }
    });

    return sections.length > 0 ? sections : ["Untitled Section"];
  };

  // Handle save operation
  const handleSave = useCallback(
    async (data: EditorSaveData): Promise<EditorSaveResult> => {
      try {
        if (!user?.id) {
          return {
            success: false,
            error: "User not authenticated",
          };
        }

        // Extract sections from blocks
        const sections = extractSectionsFromBlocks(
          data.editorState?.blocks || []
        );

        // Convert editorState to JSON string for storage
        const jsonContent = JSON.stringify(data.editorState);

        if (templateId) {
          // Update existing template
          await updateCustomTemplate(templateId, {
            name: data.name || templateName || "Untitled Template",
            description: data.description || templateDescription || "",
            content: jsonContent,
            sections: sections,
            category: "",
          });

          return {
            success: true,
            id: templateId,
            data: {
              id: templateId,
              name: data.name,
              description: data.description,
            },
          };
        } else {
          // Create new template
          const newTemplate = await createCustomTemplate({
            name: data.name || "Untitled Template",
            description: data.description || "",
            content: jsonContent,
            sections: sections,
            category: "",
            userId: user.id,
          });

          return {
            success: true,
            id: newTemplate.id,
            data: newTemplate,
          };
        }
      } catch (error: any) {
        console.error("Save failed:", error);
        return {
          success: false,
          error: error.message || "Failed to save template",
        };
      }
    },
    [user?.id, templateId, templateName, templateDescription]
  );

  // Handle load operation
  const handleLoad = useCallback(
    async (id: string): Promise<EditorLoadResult> => {
      try {
        const content = await loadTemplateContent(id);
        
        // Parse JSON content
        let parsedData;
        try {
          parsedData = typeof content === "string" ? JSON.parse(content) : content;
        } catch (e) {
          return {
            success: false,
            error: "Invalid JSON format",
          };
        }

        return {
          success: true,
          data: {
            name: templateName || "",
            description: templateDescription || "",
            editorState: parsedData,
            metadata: {},
          },
        };
      } catch (error: any) {
        return {
          success: false,
          error: error.message || "Failed to load template",
        };
      }
    },
    [templateName, templateDescription]
  );

  // Default variables for proposal templates
  const defaultVariables = [
    { name: "client_name", type: "text" as const, defaultValue: "Client Name" },
    { name: "client_email", type: "text" as const, defaultValue: "client@example.com" },
    { name: "project_name", type: "text" as const, defaultValue: "Project Name" },
    { name: "project_title", type: "text" as const, defaultValue: "Project Title" },
    { name: "date", type: "date" as const, defaultValue: new Date().toLocaleDateString() },
    { name: "status", type: "dropdown" as const, options: ["Draft", "Review", "Final"], defaultValue: "Draft" },
  ];

  // Editor configuration
  const editorConfig: TemplateEditorConfig = {
    showToolbar: !readonly,
    showSidebar: !readonly,
    showSaveButton: !readonly,
    theme: "light",
    readOnly: readonly,
    enableMultiPage: true,
    enableVariables: true,
    enableExport: true,
    exportFormats: ["html", "pdf", "json"],
    showGrid: true,
    gridSize: 20,
    snapToGrid: false,
    variables: defaultVariables,
    storageConfig,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading template...</div>
      </div>
    );
  }

  return (
    <div className={`h-full w-full flex flex-col ${className}`}>
      <ModernTemplateEditor
        templateId={templateId}
        templateName={templateName}
        templateDescription={templateDescription}
        initialData={initialData}
        config={editorConfig}
        readOnly={readonly}
        onSave={onSave || handleSave}
        onLoad={handleLoad}
        onChange={onChange}
        className="h-full w-full flex-1 min-h-0"
      />
    </div>
  );
}

